class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.querySelector('.drawer__inner');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (CartDrawerItems.pending.size) {
      this.querySelector('cart-drawer-items')?.onCartUpdate();
      this.open();
      return;
    }
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.classList.toggle('is-empty', Boolean(this.querySelector('cart-drawer-items.is-empty')));

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  static pending = new Map();
  static queue = Promise.resolve();
  static refreshPromise = null;
  static errors = new Map();
  static rendering = false;

  constructor() {
    super();
    this.addEventListener('change', (event) => {
      if (!event.target.matches('.quantity__input')) return;
      event.stopImmediatePropagation();
      if (!CartDrawerItems.rendering && !event.target.disabled) this.validateQuantity(event);
    }, true);
  }

  connectedCallback() {
    super.connectedCallback();
    this.syncPending();
  }

  static enqueue(task) {
    const result = this.queue.then(task);
    this.queue = result.catch(() => {});
    return result;
  }

  static identity(input) {
    const properties = JSON.parse(input.dataset.aesCartProperties || '{}') || {};
    const entries = Array.isArray(properties) ? properties : Object.entries(properties);
    return JSON.stringify([input.dataset.quantityVariantId, input.dataset.aesCartSellingPlan || '', entries.sort(([a], [b]) => a.localeCompare(b))]);
  }

  static findInput(operation) {
    const inputs = [...document.querySelectorAll('cart-drawer-items .quantity__input')];
    const exact = inputs.find((input) => input.dataset.quantityLineKey === operation.key);
    if (exact) return exact;
    const matches = inputs.filter((input) => this.identity(input) === operation.identity);
    return matches.length === 1 ? matches[0] : null;
  }

  syncPending() {
    this.querySelectorAll('.quantity__input').forEach((input) => {
      const row = input.closest('.cart-item');
      const operation = [...CartDrawerItems.pending.values()].find((entry) => CartDrawerItems.findInput(entry) === input);
      row.toggleAttribute('data-aes-cart-pending', Boolean(operation));
      if (operation) row.setAttribute('aria-busy', 'true');
      else row.removeAttribute('aria-busy');
      const active = document.activeElement;
      const keyboardFocus = operation && row.contains(active) && active.matches(':focus-visible');
      row.querySelectorAll('button,input').forEach((control) => {
        if (operation) {
          if (!control.hasAttribute('data-aes-was-disabled')) control.dataset.aesWasDisabled = String(control.disabled);
          control.disabled = true;
        } else if (control.hasAttribute('data-aes-was-disabled')) {
          control.disabled = control.dataset.aesWasDisabled === 'true';
          delete control.dataset.aesWasDisabled;
        }
      });
      if (operation && operation.quantity > 0) input.value = operation.quantity;
      if (keyboardFocus && active.disabled) row.querySelector('.cart-item__name')?.focus({ preventScroll: true });
      const error = row.querySelector('.cart-item__error-text');
      if (error) error.textContent = CartDrawerItems.errors.get(CartDrawerItems.identity(input)) || '';
    });
    const pending = CartDrawerItems.pending.size > 0;
    const checkout = document.getElementById('CartDrawer-Checkout');
    if (checkout) checkout.disabled = pending || this.classList.contains('is-empty');
    document.getElementById('CartDrawer-LineItemStatus')?.setAttribute('aria-hidden', String(!pending));
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Drawer-quantity-${id}`);
    if (input) input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const input = this.querySelector(`#Drawer-quantity-${line}`);
    if (!input || input.disabled || !Number.isSafeInteger(quantity) || quantity < 0) return;
    const key = input.dataset.quantityLineKey;
    if (!key || CartDrawerItems.pending.has(key)) return;
    const operation = { key, identity: CartDrawerItems.identity(input), quantity, variantId: variantId || input.dataset.quantityVariantId };
    CartDrawerItems.errors.delete(operation.identity);
    CartDrawerItems.pending.set(key, operation);
    this.syncPending();
    return CartDrawerItems.enqueue(async () => {
      let deferred;
      let applied = false;
      try {
        const current = CartDrawerItems.findInput(operation);
        if (!current) throw new Error(window.cartStrings.error);
        const items = current.closest('cart-drawer-items');
        operation.key = current.dataset.quantityLineKey;
        deferred = items.createCartLinesUpdateEvent(quantity === 0 ? 'remove' : 'update', operation.variantId, quantity, operation.key);
        const response = await fetch(routes.cart_change_url, { ...fetchConfig(), signal: AbortSignal.timeout(15000), body: JSON.stringify({
          id: operation.key, quantity, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: window.location.pathname,
        }) });
        const state = await response.json();
        if (!response.ok || state.errors) throw Object.assign(new Error(typeof state.errors === 'string' ? state.errors : state.description || window.cartStrings.error), { code: 'INVALID' });
        applied = true;
        items.resolveCartLinesUpdate(deferred, state);
        if (!CartDrawerItems.renderState(state)) await CartDrawerItems.refreshDrawer();
        const updated = CartDrawerItems.findInput(operation);
        if (quantity > 0 && updated && Number(updated.getAttribute('value')) !== quantity) {
          CartDrawerItems.errors.set(operation.identity, window.cartStrings.quantityError.replace('[quantity]', updated.getAttribute('value')));
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state, variantId: operation.variantId });
      } catch (error) {
        if (!applied) deferred?.reject(error);
        const message = error.code === 'INVALID' ? error.message : window.cartStrings.error;
        CartDrawerItems.errors.set(operation.identity, message);
        document.querySelector('cart-drawer-items')?.dispatchCartErrorEvent(message, error.code || 'SERVICE_UNAVAILABLE');
        try { await CartDrawerItems.refreshDrawer(); } catch (_) {
          const errors = document.getElementById('CartDrawer-CartErrors');
          if (errors) errors.textContent = window.cartStrings.error;
        }
      } finally {
        CartDrawerItems.pending.delete(key);
        const input = CartDrawerItems.findInput(operation);
        if (input) input.value = input.getAttribute('value');
        document.querySelector('cart-drawer-items')?.syncPending();
        const status = document.getElementById('CartDrawer-LiveRegionText');
        if (status) {
          status.removeAttribute('aria-hidden');
          status.textContent = CartDrawerItems.errors.get(operation.identity) || document.querySelector('.aes-cart-drawer-count')?.textContent || '';
        }
      }
    });
  }

  static renderState(state) {
    const html = state.sections?.['cart-drawer'];
    if (!html) return false;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector('.drawer__inner');
    const drawer = document.querySelector('cart-drawer');
    const target = drawer?.querySelector('.drawer__inner');
    if (!source || !target) return false;
    const active = document.activeElement;
    const focusInside = target.contains(active);
    const focusedInput = active.closest('.cart-item')?.querySelector('.quantity__input');
    const focusIdentity = focusedInput ? this.identity(focusedInput) : null;
    const focusName = active.getAttribute('name');
    const wasRemove = Boolean(active.closest('cart-remove-button'));
    const scrollTop = target.querySelector('cart-drawer-items')?.scrollTop || 0;
    const drafts = [...target.querySelectorAll('.quantity__input')].filter((input) => !input.disabled && input.value !== input.getAttribute('value')).map((input) => ({ key: input.dataset.quantityLineKey, identity: this.identity(input), value: input.value }));
    this.rendering = true;
    try {
      target.replaceWith(source);
      const items = source.querySelector('cart-drawer-items');
      drawer.classList.toggle('is-empty', Boolean(items?.classList.contains('is-empty')));
      items?.syncPending();
      drafts.forEach((draft) => { const input = this.findInput(draft); if (input && !input.disabled) input.value = draft.value; });
      if (items) items.scrollTop = scrollTop;
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && state.sections?.['cart-icon-bubble']) {
        const content = new DOMParser().parseFromString(state.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
        if (content) bubble.innerHTML = content.innerHTML;
      }
      if (drawer.classList.contains('active') && (focusInside || active === document.body)) {
        const row = focusIdentity && [...source.querySelectorAll('.quantity__input')].find((input) => this.identity(input) === focusIdentity)?.closest('.cart-item');
        const candidate = row && (wasRemove ? row.querySelector('cart-remove-button button') : focusName ? [...row.querySelectorAll('[name]')].find((control) => control.name === focusName) : row.querySelector('.cart-item__name'));
        trapFocus(source, candidate && !candidate.disabled ? candidate : source.querySelector('.drawer__close'));
        if (items) items.scrollTop = scrollTop;
      }
      return true;
    } finally { this.rendering = false; }
  }

  static async refreshDrawer() {
    const response = await fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`, { signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error(window.cartStrings.error);
    if (!this.renderState({ sections: await response.json() })) throw new Error(window.cartStrings.error);
  }

  onCartUpdate() {
    if (CartDrawerItems.refreshPromise) return CartDrawerItems.refreshPromise;
    CartDrawerItems.refreshPromise = CartDrawerItems.enqueue(() => CartDrawerItems.refreshDrawer())
      .catch(() => {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      }).finally(() => { CartDrawerItems.refreshPromise = null; });
    return CartDrawerItems.refreshPromise;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
